import React from 'react'
import cx from 'classnames'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import { l, Popover, Icon } from '@manychat/manyui'

import ConditionSummary from 'common/filter/components/Condition/ConditionSummary'
import FilterFieldsSearch from 'common/filter/components/FilterFieldsSearch'
import cm from 'common/filter/components/Filters/Filters.module.css'
import {
  FieldTypesOperators,
  UNARY_OPERATORS,
  FilterField,
  FieldTypeDefaultOperators,
} from 'common/filter/models/AudienceFilter/constants'
import formatFilterCondition from 'common/filter/models/AudienceFilter/formatters'
import { createCondition } from 'common/filter/models/AudienceFilter/helpers'

import ConditionEditor from './ConditionEditor'

export default class Condition extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    summaryClassName: PropTypes.string,
    allowRemove: PropTypes.bool,
    allowDateFormula: PropTypes.bool,
    segments: PropTypes.object,
    fields: PropTypes.array,
    botTimezone: PropTypes.string,
  }

  static defaultProps = {
    allowRemove: true,
    isBuilder: false,
    segments: {},
    fields: [],
  }

  constructor(props, context) {
    super(props, context)

    this.state = {
      removed: false,
      condition: props.condition,
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timer)
  }

  componentDidUpdate(prevProps) {
    const { condition: prevCondition } = prevProps
    const { condition: nextCondition } = this.props

    if (
      prevCondition !== nextCondition ||
      (prevCondition && nextCondition && prevCondition.value !== nextCondition.value)
    ) {
      this.setState({ condition: nextCondition })
    }
  }

  handleValueRef = (el) => {
    const { operator } = this.props.condition

    const { value } = this.props.condition

    if (
      !this.valueRef &&
      (value == null || (value && value.length === 0)) &&
      !UNARY_OPERATORS.includes(operator)
    ) {
      this.openValuePopover(el)
    }
    this.valueRef = el
  }

  handleRemoveClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const { onRemove, condition, groupIndex } = this.props
    this.timer = setTimeout(() => {
      onRemove(groupIndex, condition._oid)
    }, 250)
    this.setState({ removed: true })
  }

  openValuePopover = (anchor) =>
    this.setState({ valuePopoverAnchor: anchor, fieldPopoverAnchor: null })

  saveCondition = () => {
    const { onChange, groupIndex } = this.props
    const { condition } = this.state

    onChange(groupIndex, condition._oid, condition)
  }
  closeValuePopover = (_) => {
    this.setState({ valuePopoverAnchor: null })
    this.saveCondition()
  }

  openFieldPopover = (anchor) => this.setState({ fieldPopoverAnchor: anchor })
  closeFieldPopover = (_) => {
    this.setState({ fieldPopoverAnchor: null })
    this.saveCondition()
  }
  getCallbackOnFieldSelect = (field) => {
    if (FieldTypeDefaultOperators[field.type]) return this.closeFieldPopover
    return () => this.openValuePopover(this.valueRef)
  }

  handleFieldSelect = (field) => {
    const { isBuilder } = this.props
    const condition = createCondition(field, null, null, { isBuilder })
    condition._oid = this.props.condition._oid
    const callback = this.getCallbackOnFieldSelect(field)
    this.setState({ condition }, () => callback())
  }

  handleChange = (data, closePopover) => {
    const condition = { ...this.state.condition, ...data }
    this.setState({ condition }, () => {
      if (closePopover) {
        this.closeValuePopover()
      }
    })
  }

  handleFieldLinkClick = (e) => this.openFieldPopover(e.currentTarget)
  handleValueLinkClick = (e) => this.openValuePopover(e.currentTarget)

  renderFieldPopover = () => {
    const { fieldPopoverAnchor } = this.state
    const { fields, isBuilder } = this.props

    return (
      <Popover
        key="condition-field-popover"
        open={fieldPopoverAnchor != null}
        anchor={fieldPopoverAnchor}
        anchorOrigin={'left top'}
        targetOrigin={'left top'}
        onRequestClose={this.closeFieldPopover}
      >
        <FilterFieldsSearch
          onSelect={this.handleFieldSelect}
          fields={fields}
          isBuilder={isBuilder}
        />
      </Popover>
    )
  }

  renderRemoveButton = () => (
    <span key="condition-remove-button" onClick={this.handleRemoveClick}>
      <Icon.Close size={16} fill={null} className={cm.removeIcon} />
    </span>
  )

  renderError = (error) => {
    const { disabled, removed, allowRemove, className, summaryClassName } = this.props

    return (
      <div
        className={cx(cm.condition, className, {
          [cm.conditionDisabled]: disabled,
          [cm.conditionRemoved]: removed,
        })}
      >
        <ConditionSummary
          valueRef={this.handleValueRef}
          disabled={disabled}
          className={summaryClassName}
          error={error}
          onFieldLinkClick={this.handleFieldLinkClick}
        />
        {!disabled && (
          <>
            {allowRemove && this.renderRemoveButton()}
            {this.renderFieldPopover()}
          </>
        )}
      </div>
    )
  }

  render() {
    const { condition: conditionSource } = this.state
    const { removed, valuePopoverAnchor } = this.state

    const {
      fields,
      segments,
      activeReasons,
      usedFields,
      disabled,
      className,
      summaryClassName,
      allowRemove,
      isBuilder,
      useChangedLabelForOperators,
      botTimezone,
    } = this.props

    // Hack for supporting old operators
    const condition = { ...conditionSource }
    const operatorViewAliases = { IN: 'IS', NOT_IN: 'IS_NOT', 'NOT IN': 'IS_NOT' }

    if (condition.operator in operatorViewAliases) {
      condition.originOperator = condition.operator
      condition.operator = operatorViewAliases[condition.operator]
    }

    const isRnType = [
      FilterField.IG_RECURRING_NOTIFICATION_OPTIN,
      FilterField.IG_RECURRING_NOTIFICATION,
      FilterField.ONE_TIME_NOTIFICATION_OPTIN,
      FilterField.ONE_TIME_NOTIFICATION,
    ].includes(condition.type)

    if (
      condition.type === 'tag' &&
      condition.value &&
      !get(segments, `tag_${condition.value}.active`, false)
    ) {
      return this.renderError(l.translate('Tag is missing'))
    }

    if (isRnType && condition.value) {
      const isActiveReasonExist = activeReasons.some((r) => r.reason_id === condition.value)
      if (!isActiveReasonExist) {
        return this.renderError(l.translate('Notification Topic is missing'))
      }
    }

    const fieldDesc = fields.find((f) => f.id === condition.field)

    if (!fieldDesc) {
      return this.renderError(l.translate('Field is missing'))
    }

    const operators = FieldTypesOperators[fieldDesc.type]

    const operatorValue = condition.operator || operators[0].value
    const operatorDesc = operators.find(
      (op) => op.value === operatorValue || op.value === condition.originOperator,
    )

    // eslint-disable-next-line no-unused-vars
    const [, operator, formattedValue] = formatFilterCondition(
      condition.value,
      fieldDesc,
      operatorDesc,
      segments,
      { useChangedLabelForOperators, botTimezone },
    )

    return (
      <div
        className={cx(cm.condition, className, {
          [cm.conditionDisabled]: disabled,
          [cm.conditionRemoved]: removed,
        })}
      >
        <ConditionSummary
          valueRef={this.handleValueRef}
          icon={fieldDesc.segmentType === 'cuf' ? fieldDesc.type : ''}
          caption={fieldDesc.caption}
          operator={operator}
          value={formattedValue}
          ignoreValue={operatorDesc.ignoreValue}
          validValue={
            formattedValue !== ''
              ? Boolean(`${condition.value}` || operatorDesc.ignoreValue)
              : UNARY_OPERATORS.includes(operatorDesc.value)
          }
          className={summaryClassName}
          disabled={disabled}
          onFieldLinkClick={this.handleFieldLinkClick}
          onValueLinkClick={this.handleValueLinkClick}
        />
        {!disabled && (
          <>
            {allowRemove && this.renderRemoveButton()}
            {this.renderFieldPopover()}
            <Popover
              open={valuePopoverAnchor != null}
              anchor={valuePopoverAnchor}
              autoPosition="auto"
              anchorOrigin="bottom left"
              popoverOrigin="top left"
              onRequestClose={this.closeValuePopover}
            >
              <ConditionEditor
                condition={condition}
                operatorDesc={operatorDesc}
                fieldDesc={fieldDesc}
                usedFields={usedFields}
                onChange={this.handleChange}
                onRequestClose={this.closeValuePopover}
                useChangedLabelForOperators={this.props.useChangedLabelForOperators}
                allowDateFormula={this.props.allowDateFormula}
                isBuilder={isBuilder}
                botTimezone={botTimezone}
              />
            </Popover>
          </>
        )}
      </div>
    )
  }
}
